import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout, Alert, Button, Card, CardBody, Spinner } from '../components';
import { familyTreesAPI, treeMembersAPI, TreeMemberDto } from '../services/api';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import { brandClasses } from '../theme';
import { useFormatDate } from '../i18n';

const isForbiddenError = (error: unknown) => {
  return typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
    (error as { response?: { status?: number } }).response?.status === 403;
};

const extractPayload = <T,>(payload: T | { data?: T | { data?: T } }): T => {
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    const wrapped = payload as { data?: T | { data?: T } };
    return (typeof wrapped.data === 'object' && wrapped.data !== null && 'data' in wrapped.data
      ? (wrapped.data as { data?: T }).data
      : wrapped.data) as T;
  }

  return payload as T;
};

export const MembersPage: React.FC = () => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const { treeId } = useParams<{ treeId: string }>();
  const navigate = useNavigate();
  const { setNavigationState, clearNavigationState } = useAppNavigation();
  const [members, setMembers] = useState<TreeMemberDto[]>([]);
  const [treeName, setTreeName] = useState('Current Tree');
  const [isLoading, setIsLoading] = useState(true);
  const [permissionState, setPermissionState] = useState<'unknown' | 'allowed' | 'denied'>('unknown');
  const [error, setError] = useState('');

  const loadMembersPage = useCallback(async () => {
    if (!treeId) {
      setError(t('errors.treeNotFound', { ns: 'members' }));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setPermissionState('unknown');
      setMembers([]);

      const treeResponse = await familyTreesAPI.getTreeAccess(treeId);
      const tree = treeResponse.data.data ?? treeResponse.data;

      setTreeName(tree?.name || t('fallbackTreeName', { ns: 'members' }));

      const nextHasManagePermission = Boolean(tree?.canManageMembers);
      setPermissionState(nextHasManagePermission ? 'allowed' : 'denied');

      if (!nextHasManagePermission) {
        return;
      }

      try {
        const membersResponse = await treeMembersAPI.getMembers(treeId);
        setMembers(extractPayload<TreeMemberDto[]>(membersResponse.data) ?? []);
      } catch (membersError) {
        if (isForbiddenError(membersError)) {
          setPermissionState('denied');
          setMembers([]);
          return;
        }

        setError(t('errors.loadFailed', { ns: 'members' }));
      }
    } catch (err) {
      if (isForbiddenError(err)) {
        setPermissionState('denied');
        setMembers([]);
      } else {
        setError(t('errors.loadFailed', { ns: 'members' }));
      }
    } finally {
      setIsLoading(false);
    }
  }, [t, treeId]);

  useEffect(() => {
    loadMembersPage();
  }, [loadMembersPage]);

  useEffect(() => {
    if (!treeId) {
      clearNavigationState();
      return;
    }

    setNavigationState({
      activeTreeId: treeId,
      canManageMembers: permissionState === 'allowed',
      canManagePublicTree: permissionState === 'allowed',
    });

    return () => {
      clearNavigationState();
    };
  }, [clearNavigationState, permissionState, setNavigationState, treeId]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${brandClasses.titleText}`}>{t('title', { ns: 'members' })}</h1>
            <p className={`mt-2 text-sm ${brandClasses.mutedText}`}>
              {t('subtitle', { ns: 'members', treeName })}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(treeId ? `/canvas/${treeId}` : '/trees')}
          >
            {t('buttons.backToCurrentTree', { ns: 'common' })}
          </Button>
        </div>

        {error && <Alert variant="error" message={error} />}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Spinner size="lg" className="mb-4" />
            <p className={`text-lg ${brandClasses.mutedText}`}>{t('status.loadingMembers', { ns: 'members' })}</p>
          </div>
        ) : permissionState === 'denied' ? (
          <Card className={brandClasses.elevatedCard}>
            <CardBody className="space-y-3">
              <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('access.restrictedTitle', { ns: 'members' })}</h2>
              <p className={`text-sm ${brandClasses.bodyText}`}>
                {t('access.restrictedDescription', { ns: 'members' })}
              </p>
            </CardBody>
          </Card>
        ) : permissionState === 'allowed' ? (
          <Card className={brandClasses.elevatedCard}>
            <CardBody className="space-y-4">
              <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('sections.treeMembers', { ns: 'members' })}</h2>
              <div className="space-y-3">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}
                  >
                    <div>
                      <p className={`font-medium ${brandClasses.primaryText}`}>{member.userEmail}</p>
                      <p className={`text-sm ${brandClasses.subtleText}`}>
                        {t('memberSince', {
                          ns: 'members',
                          role: member.role,
                          date: formatDate(member.joinedAt),
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ) : null}
      </div>
    </MainLayout>
  );
};
